// pages/Project/Validation.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import { Tabs, Tab } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Select from 'react-select';

import PaginatedListTable from '../../components/PaginatedListTable';
import ImageViewerModal from '../../components/ImageViewerModal';
import ViewEngagementModal from '../../components/ViewEngagementModal';
import Container from '../../layouts/Container';

import api from '../../services/api';
import { getToday } from '../../utils/dateUtil';
import { useProjects } from '../../context/ProjectContext';
import { useUsers } from '../../context/UserContext';

import { getEngagementsByFormGroup } from '../../services/project/formGroupService';
import { updateEngagementStatus } from '../../services/project/engagementService';
import { deleteEngagement } from '../../services/project/engagementService';
import { getPublicValidationStatuses } from '../../services/project/validationStatusService';
import { getInventoryFilters } from '../../services/project/inventoryService';
import AnchorTag from '../../components/AnchorTag';
import { XOctagon } from 'react-bootstrap-icons';

function Validation() {
  const [activeTab, setActiveTab] = useState('unvalidated');
  // Handler for Validate button
  const handleValidate = async (engagement, remark = '') => {
    try {
      await updateEngagementStatus(engagement.id, 'validated', 'validationRemarks', remark);
      toast.success('Validated!');
      setShowModal(false);
      validatedTableRef.current?.refetch();
      approvedTableRef.current?.refetch();
    } catch (err) {
      toast.error('Validation failed');
    }
  };

  // Handler for Reject button
  const handleReject = async (engagement, remark = '') => {
    try {
      await updateEngagementStatus(engagement.id, 'reject', 'validationRemarks', remark);
      toast.error('Rejected!');
      setShowModal(false);
      validatedTableRef.current?.refetch();
      approvedTableRef.current?.refetch();
    } catch (err) {
      toast.error('Rejection failed');
    }
  };
  const approvedTableRef = useRef();
  const validatedTableRef = useRef();
  const noEditUnvalidatedTableRef = useRef();
  const noEditApproved1stTableRef = useRef();
  const noEditPendingTableRef = useRef();
  const noEditApprovedTableRef = useRef();
  const noEditRejectedTableRef = useRef();
  const { projectId } = useParams();
  const { projects, isProjectReadOnly } = useProjects();
  const { isSuperAdmin } = useUsers();
  const currentProject = projects?.find(p => String(p.id) === String(projectId));
  const isApprovalEnabled = currentProject?.settings?.approval === true;
  const isNoEditMode = currentProject?.settings?.approval_validation_no_edit === true;

  const [startDate, setStartDate] = useState(getToday());
  const [endDate, setEndDate] = useState(getToday());
  const [formGroups, setFormGroups] = useState([]);
  const [selectedFormGroupId, setSelectedFormGroupId] = useState(null);
  const [forms, setForms] = useState([]);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [engagement, setEngagement] = useState(null);
  const [validationStatuses, setValidationStatuses] = useState({
    Validated: [],
    Invalid: [],
  });
  const [loadingFG, setLoadingFG] = useState(true);
  const [status, setStatus] = useState('');
  const [filterData, setFilterData] = useState([]);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [loadingApproved, setLoadingApproved] = useState(false);
  const [loadingNoEditUnvalidated, setLoadingNoEditUnvalidated] = useState(false);
  const [loadingNoEditApproved1st, setLoadingNoEditApproved1st] = useState(false);
  const [loadingValidated, setLoadingValidated] = useState(false);

  const disapprovedTableRef = useRef();
  const [loadingDisapproved, setLoadingDisapproved] = useState(false);
  const rejectedTableRef = useRef();
  const [loadingRejected, setLoadingRejected] = useState(false);

  useEffect(() => {
    getInventoryFilters()
      .then(res => {
        const data = res?.data || {};
        setFilterData(data[projectId] || []);
      })
      .catch(() => setFilterData([]));
  }, [projectId]);

  const areaOptions = useMemo(() => {
    if (!selectedRegions.length) {
      return filterData.flatMap(r => (r.areas || []).map(a => ({ value: a.id, label: a.name })));
    }
    const selectedIds = selectedRegions.map(r => String(r.value));
    return filterData
      .filter(r => selectedIds.includes(String(r.id)))
      .flatMap(r => (r.areas || []).map(a => ({ value: a.id, label: a.name })));
  }, [filterData, selectedRegions]);

  useEffect(() => {
    const init = async () => {
      try {
        const res = await api.get(`/projects/${projectId}/formgroups`);
        setFormGroups(res.data || []);
        const activeGroup = (res.data || []).find(g => g.isActive) || (res.data || [])[0];
        if (activeGroup) {
          const fgId = String(activeGroup.id);
          setSelectedFormGroupId(fgId);
          await fetchForms(fgId);
        }
      } catch (err) {
        console.error(err);
        toast.error('Failed to load form groups');
      } finally {
        setLoadingFG(false);
      }
    };
    init();
  }, [projectId]);

  useEffect(() => {
    const loadValidationStatuses = async () => {
      try {
        const response = await getPublicValidationStatuses(projectId);
        setValidationStatuses({
          Validated: Array.isArray(response?.Validated) ? response.Validated : [],
          Invalid: Array.isArray(response?.Invalid) ? response.Invalid : [],
        });
      } catch (err) {
        console.error(err);
        toast.error('Failed to load validation statuses');
        setValidationStatuses({ Validated: [], Invalid: [] });
      }
    };

    loadValidationStatuses();
  }, [projectId]);

  const submitFirstValidationReview = async (row, review) => {
    if (!review?.status || !review?.remark) {
      toast.error('Please select a status');
      return;
    }

    if (review.status === 'Validated') {
      await handleValidate(row, review.remark);
      return;
    }

    await handleReject(row, review.remark);
  };

  const fetchForms = async (formGroupId) => {
    try {
      const res = await api.get(`/projects/${projectId}/formgroups/${formGroupId}`);
      setForms(res.data || []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load forms');
    }
  };

  const handleApply = async () => {
    if (selectedFormGroupId) await fetchForms(selectedFormGroupId);
    if (isNoEditMode) {
      noEditUnvalidatedTableRef.current?.refetch();
      noEditApproved1stTableRef.current?.refetch();
      noEditPendingTableRef.current?.refetch();
      noEditApprovedTableRef.current?.refetch();
      noEditRejectedTableRef.current?.refetch();
    } else {
      approvedTableRef.current?.refetch();
      validatedTableRef.current?.refetch();
      if (isApprovalEnabled) {
        disapprovedTableRef.current?.refetch();
      }
      rejectedTableRef.current?.refetch();
    }
  };

  const [loadingNoEditPending, setLoadingNoEditPending] = useState(false);
  const [loadingNoEditApproved, setLoadingNoEditApproved] = useState(false);
  const [loadingNoEditRejected, setLoadingNoEditRejected] = useState(false);

  const fetchNoEditEngagementsByStatus = (fixedStatus, setLoading) => async ({ pageIndex, pageSize, search } = {}) => {
    setLoading(true);
    try {
      const startDateUTC = new Date(`${startDate}T00:00:00+08:00`).toISOString();
      const endDateUTC = new Date(`${endDate}T23:59:59+08:00`).toISOString();
      const res = await api.get('/validations', {
        params: {
          projectId,
          formGroupId: selectedFormGroupId,
          startDate: startDateUTC,
          endDate: endDateUTC,
          page: (pageIndex ?? 0) + 1,
          limit: pageSize ?? 10,
          ...(search ? { search } : {}),
          status: fixedStatus,
          ...(selectedRegions.length ? { regionIds: selectedRegions.map(r => r.value).join(',') } : {}),
          ...(selectedAreas.length ? { areaIds: selectedAreas.map(a => a.value).join(',') } : {}),
        },
      });
      const payload = res.data;
      const rows = payload?.results ?? payload?.data ?? [];
      const pagination = payload?.pagination ?? {};
      return {
        data: rows,
        pageCount: pagination.totalPages ?? 1,
        totalItems: pagination.totalCount ?? rows.length,
      };
    } catch (err) {
      console.error(err);
      toast.error('Error fetching validations.');
      return { data: [], pageCount: 0, totalItems: 0 };
    } finally {
      setLoading(false);
    }
  };

  const fetchNoEditPendingEngagements = fetchNoEditEngagementsByStatus('1st_validated', setLoadingNoEditPending);
  const fetchNoEditApprovedEngagements = fetchNoEditEngagementsByStatus('2nd_validated', setLoadingNoEditApproved);
  const fetchNoEditRejectedEngagements = fetchNoEditEngagementsByStatus('2nd_rejected', setLoadingNoEditRejected);

  const fetchNoEditUnvalidatedEngagements = async (params) => {
    setLoadingNoEditUnvalidated(true);
    try {
      const startDateUTC = new Date(`${startDate}T00:00:00+08:00`).toISOString();
      const endDateUTC = new Date(`${endDate}T23:59:59+08:00`).toISOString();
      const res = await getEngagementsByFormGroup(projectId, {
        formGroupId: selectedFormGroupId,
        startDate: startDateUTC,
        endDate: endDateUTC,
        status: 'pending',
        ...params,
      });
      return res;
    } catch (err) {
      console.error(err);
      toast.error('Error fetching unvalidated engagements.');
      return { data: [], pageCount: 0, totalItems: 0 };
    } finally {
      setLoadingNoEditUnvalidated(false);
    }
  };

  const fetchNoEditApproved1stEngagements = fetchNoEditEngagementsByStatus('unvalidated', setLoadingNoEditApproved1st);

  const submitSecondValidation = async (row, review) => {
    try {
      if (!review?.status || !review?.remark) {
        toast.error('Please select a status');
        return;
      }

      const normalizedStatus = review.status === 'Validated' ? 'Validated' : 'Invalid';
      const decision = normalizedStatus === 'Validated' ? 'approved' : 'disapproved';

      const validationId = row?.validationId || row?.id;
      await api.patch(`/validations/${validationId}`, {
        '2nd_description': review.remark,
        '2nd_status': normalizedStatus,
        '2nd_validation': decision,
      });

      toast.success(decision === 'approved' ? 'Approved!' : 'Disapproved!');
      setShowModal(false);
      noEditPendingTableRef.current?.refetch();
      noEditApprovedTableRef.current?.refetch();
      noEditRejectedTableRef.current?.refetch();
    } catch (err) {
      console.error(err);
      toast.error('Failed to submit validation status');
    }
  };

  const [revertRow, setRevertRow] = useState(null);
  const [revertLoading, setRevertLoading] = useState(false);

  const handleRevertToUnvalidated = async () => {
    if (!revertRow || !isSuperAdmin) return;
    setRevertLoading(true);
    try {
      const validationId = revertRow?.validationId || revertRow?.id;
      await api.patch(`/validations/${validationId}`, {
        '2nd_status': null,
        '2nd_description': null,
        '2nd_validation': null,
      });
      toast.success('Reverted to pending validation.');
      setRevertRow(null);
      noEditPendingTableRef.current?.refetch();
      noEditRejectedTableRef.current?.refetch();
    } catch (err) {
      console.error(err);
      toast.error('Failed to revert validation.');
    } finally {
      setRevertLoading(false);
    }
  };


  const handleDelete = async (engagementId, tab) => {
    if (!window.confirm('Are you sure you want to delete this engagement?')) return;
    try {
      const response = await deleteEngagement(engagementId);
      if (!response.success) {
        toast.error(response.data?.error || 'Delete failed');
        return;
      }
      if (tab === 'unvalidated') {
        approvedTableRef.current?.refetch();
      } else if (tab === 'validated') {
        validatedTableRef.current?.refetch();
      } else if (tab === 'disapproved') {
        disapprovedTableRef.current?.refetch();
      } else {
        rejectedTableRef.current?.refetch();
      }
      toast.success(response.message || 'Deleted');
    } catch (error) {
      console.error('Error deleting engagement:', error);
      toast.error(error.response?.data?.errorMessage || 'Something went wrong');
    }
  };

  const viewEngagement = (row) => {
    setEngagement(row);
    setShowModal(true);
  };

  const handleImageClick = (imageSrc, footer) => {
    setSelectedImage({
      src: imageSrc,
      footer
    });
    setShowImageModal(true);
  };

  const columnHelper = createColumnHelper();
  const fieldColumns = useMemo(() => {
    const formWithFields = forms.find(f => Array.isArray(f.fields) && f.fields.length > 0);
    if (!formWithFields) return [];
    return formWithFields.fields
      .filter(field => field.type !== 'label' && field.type !== 'address')
      .slice(0, 4)
      .map(field =>
        columnHelper.accessor(
          row => {
            const value = row.fields?.[field.name];
            return value;
          },
          {
            id: field.name,
            header: field.label || field.name,
            cell: info => {
              const value = info.getValue();
              if (field.type === 'image') {
                return value ? (
                  <img
                    src={value?.uri?.[0]}
                    alt=""
                    style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4 }}
                    onClick={() => handleImageClick(value)}
                  />
                ) : 'No image';
              }
              return value;
            },
          }
        )
      );
  }, [forms]);

  const detailColumns = [
    columnHelper.accessor(
      row => row.user?.name || `${row.User?.firstName || ''} ${row.User?.lastName || ''}`.trim(),
      {
        id: 'fullName',
        header: 'Reported By',
      }
    ),
    columnHelper.accessor(row => row.createdAt, {
      id: 'createdAt',
      header: 'Synced Date',
      cell: info => new Date(info.getValue()).toLocaleString(),
    }),
    columnHelper.accessor(row => row.submittedAt, {
      id: 'submittedAt',
      header: 'Submitted Date',
      cell: info => info.getValue() ? new Date(info.getValue()).toLocaleString() : '—',
    }),
    columnHelper.accessor(row => row.approverName, {
      id: 'approverName',
      header: 'Approver',
      cell: info => info.getValue() || '—',
    }),
    columnHelper.accessor(row => row['1st_validator'] || row.validatorName, {
      id: '1st_validator',
      header: '1st Validator',
      cell: info => info.getValue() || '—',
    }),
    isNoEditMode && columnHelper.accessor(row => row['1st_status'], {
      id: '1st_status',
      header: '1st Validation Status',
      cell: info => info.getValue() || '—',
    }),
    isNoEditMode && columnHelper.accessor(row => row['1st_description'], {
      id: '1st_description',
      header: '1st Validation Description',
      cell: info => info.getValue() || '—',
    }),
    isNoEditMode && columnHelper.accessor(row => row['2nd_validator'] || row.secondValidatorName, {
      id: '2nd_validator',
      header: '2nd Validator',
      cell: info => info.getValue() || '—',
    }),
    isNoEditMode && columnHelper.accessor(row => row['2nd_status'], {
      id: '2nd_status',
      header: '2nd Validation Status',
      cell: info => info.getValue() || '—',
    }),
    isNoEditMode && columnHelper.accessor(row => row['2nd_description'], {
      id: '2nd_description',
      header: '2nd Validation Description',
      cell: info => info.getValue() || '—',
    }),
  ].filter(Boolean);

  const unvalidatedDetailColumns = [
    columnHelper.accessor(
      row => row.user?.name || `${row.User?.firstName || ''} ${row.User?.lastName || ''}`.trim(),
      {
        id: 'fullName',
        header: 'Reported By',
      }
    ),
    columnHelper.accessor(row => row.createdAt, {
      id: 'createdAt',
      header: 'Synced Date',
      cell: info => new Date(info.getValue()).toLocaleString(),
    }),
    columnHelper.accessor(row => row.submittedAt, {
      id: 'submittedAt',
      header: 'Submitted Date',
      cell: info => info.getValue() ? new Date(info.getValue()).toLocaleString() : '—',
    }),
    columnHelper.accessor(row => row.approverName, {
      id: 'approverName',
      header: 'Approver',
      cell: info => info.getValue() || '—',
    }),
    columnHelper.accessor(row => row['1st_validator'] || row.validatorName, {
      id: '1st_validator',
      header: '1st Validator',
      cell: info => info.getValue() || '—',
    }),
  ];

  const rejectedActionColumn = columnHelper.display({
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => (
      <Button
        variant="outline-warning"
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          setRevertRow(row.original);
        }}
      >
        Revert
      </Button>
    ),
  });


  const fetchDisapprovedEngagements = async (params) => {
    setLoadingDisapproved(true);
    try {
      const startDateUTC = new Date(`${startDate}T00:00:00+08:00`).toISOString();
      const endDateUTC = new Date(`${endDate}T23:59:59+08:00`).toISOString();
      const res = await getEngagementsByFormGroup(projectId, {
        formGroupId: selectedFormGroupId,
        startDate: startDateUTC,
        endDate: endDateUTC,
        status: 'disapproved',
        ...params,
      });
      return res;
    } catch (err) {
      console.error(err);
      toast.error('Error fetching engagements.');
      return { data: [], totalPages: 0 };
    } finally {
      setLoadingDisapproved(false);
    }
  };

  const fetchRejectedEngagements = async (params) => {
    setLoadingRejected(true);
    try {
      const startDateUTC = new Date(`${startDate}T00:00:00+08:00`).toISOString();
      const endDateUTC = new Date(`${endDate}T23:59:59+08:00`).toISOString();
      const res = await getEngagementsByFormGroup(projectId, {
        formGroupId: selectedFormGroupId,
        startDate: startDateUTC,
        endDate: endDateUTC,
        status: 'reject',
        ...params,
      });
      return res;
    } catch (err) {
      console.error(err);
      toast.error('Error fetching engagements.');
      return { data: [], totalPages: 0 };
    } finally {
      setLoadingRejected(false);
    }
  };

  // Fetchers for two tabs only
  const fetchApprovedEngagements = async (params) => {
    setLoadingApproved(true);
    try {
      const startDateUTC = new Date(`${startDate}T00:00:00+08:00`).toISOString();
      const endDateUTC = new Date(`${endDate}T23:59:59+08:00`).toISOString();
      const res = await getEngagementsByFormGroup(projectId, {
        formGroupId: selectedFormGroupId,
        startDate: startDateUTC,
        endDate: endDateUTC,
        status: 'approved',
        ...params,
      });
      return res;
    } catch (err) {
      console.error(err);
      toast.error('Error fetching engagements.');
      return { data: [], totalPages: 0 };
    } finally {
      setLoadingApproved(false);
    }
  };

  const fetchValidatedEngagements = async (params) => {
    setLoadingValidated(true);
    try {
      const startDateUTC = new Date(`${startDate}T00:00:00+08:00`).toISOString();
      const endDateUTC = new Date(`${endDate}T23:59:59+08:00`).toISOString();
      const res = await getEngagementsByFormGroup(projectId, {
        formGroupId: selectedFormGroupId,
        startDate: startDateUTC,
        endDate: endDateUTC,
        status: 'validated',
        ...params,
      });
      return res;
    } catch (err) {
      console.error(err);
      toast.error('Error fetching engagements.');
      return { data: [], totalPages: 0 };
    } finally {
      setLoadingValidated(false);
    }
  };

  useEffect(() => {
    if (!isApprovalEnabled && activeTab === 'disapproved') {
      setActiveTab('unvalidated');
    }
  }, [activeTab, isApprovalEnabled]);

  useEffect(() => {
    if (isNoEditMode && activeTab === 'unvalidated') {
      setActiveTab('noEditUnvalidated');
    }
  }, [isNoEditMode]);

  return (
    <Container>
      <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
        <h2 className="mb-0">Validation</h2>
        <Form className="d-flex align-items-center gap-3 flex-wrap mb-0">
          <Form.Group className="d-flex align-items-center gap-2 mb-0">
            <Form.Label className="mb-0">Start</Form.Label>
            <Form.Control
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="d-flex align-items-center gap-2 mb-0">
            <Form.Label className="mb-0">End</Form.Label>
            <Form.Control
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
            />
          </Form.Group>
          {!isNoEditMode && (
            <Form.Group className="d-flex align-items-center gap-2 mb-0">
              <Form.Label className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                Status
              </Form.Label>
              <Form.Select
                value={status}
                onChange={e => setStatus(e.target.value)}
              >
                <option value="">All</option>
                <option value="1st_validated">1st Level Validation - Validated</option>
                <option value="1st_rejected">1st Level Validation - Rejected</option>
                <option value="2nd_validated">2nd Level Validation - Validated</option>
                <option value="2nd_rejected">2nd Level Validation - Rejected</option>
              </Form.Select>
            </Form.Group>
          )}
          <Select
            isMulti
            options={filterData.map(r => ({ value: r.id, label: r.name }))}
            value={selectedRegions}
            onChange={selected => {
              setSelectedRegions(selected || []);
              setSelectedAreas([]);
            }}
            placeholder="All Regions"
            menuPortalTarget={document.body}
            styles={{ container: b => ({ ...b, minWidth: 200 }) }}
          />
          <Select
            isMulti
            options={areaOptions}
            value={selectedAreas}
            onChange={selected => setSelectedAreas(selected || [])}
            placeholder="All Areas"
            menuPortalTarget={document.body}
            styles={{ container: b => ({ ...b, minWidth: 200 }) }}
          />
          <Button variant="primary" type="button" onClick={handleApply}>
            Apply
          </Button>
        </Form>
      </div>
      {isNoEditMode ? (
        <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-3">
          <Tab eventKey="noEditUnvalidated" title="Pending Approval">
            {loadingFG ? (
              <p>Loading...</p>
            ) : (
              <PaginatedListTable
                ref={noEditUnvalidatedTableRef}
                tableId="no-edit-unvalidated-list"
                columns={[...fieldColumns, ...unvalidatedDetailColumns]}
                rowIdAccessor={row => row.id}
                onRowClick={viewEngagement}
                onFetchData={fetchNoEditUnvalidatedEngagements}
                defaultSort={[["submittedAt", "DESC"]]}
                isLoading={loadingNoEditUnvalidated}
                hideSearch
              />
            )}
          </Tab>
          <Tab eventKey="noEditApproved1st" title="Pending Validation (1st Level)">
            {loadingFG ? (
              <p>Loading...</p>
            ) : (
              <PaginatedListTable
                ref={noEditApproved1stTableRef}
                tableId="no-edit-approved-1st-list"
                columns={[...fieldColumns, ...detailColumns]}
                rowIdAccessor={row => row.id}
                onRowClick={viewEngagement}
                onFetchData={fetchNoEditApproved1stEngagements}
                defaultSort={[["submittedAt", "DESC"]]}
                isLoading={loadingNoEditApproved1st}
                hideSearch
              />
            )}
          </Tab>
          <Tab eventKey="unvalidated" title="Pending Validation (2nd Level)">
            {loadingFG ? (
              <p>Loading...</p>
            ) : (
              <PaginatedListTable
                ref={noEditPendingTableRef}
                tableId="no-edit-pending-list"
                columns={[...fieldColumns, ...detailColumns]}
                rowIdAccessor={row => row.id}
                onRowClick={viewEngagement}
                onFetchData={fetchNoEditPendingEngagements}
                defaultSort={[["submittedAt", "DESC"]]}
                isLoading={loadingNoEditPending}
                hideSearch
              />
            )}
          </Tab>
          <Tab eventKey="validated" title="HQ Validated">
            {loadingFG ? (
              <p>Loading...</p>
            ) : (
              <PaginatedListTable
                ref={noEditApprovedTableRef}
                tableId="no-edit-approved-list"
                columns={[...fieldColumns, ...detailColumns]}
                rowIdAccessor={row => row.id}
                onRowClick={viewEngagement}
                onFetchData={fetchNoEditApprovedEngagements}
                defaultSort={[["submittedAt", "DESC"]]}
                isLoading={loadingNoEditApproved}
                hideSearch
              />
            )}
          </Tab>
          <Tab eventKey="rejected" title="Rejected">
            {loadingFG ? (
              <p>Loading...</p>
            ) : (
              <PaginatedListTable
                ref={noEditRejectedTableRef}
                tableId="no-edit-rejected-list"
                columns={[...fieldColumns, ...detailColumns, ...(isSuperAdmin ? [rejectedActionColumn] : [])]}
                rowIdAccessor={row => row.id}
                onRowClick={viewEngagement}
                onFetchData={fetchNoEditRejectedEngagements}
                defaultSort={[["submittedAt", "DESC"]]}
                isLoading={loadingNoEditRejected}
                hideSearch
              />
            )}
          </Tab>
        </Tabs>
      ) : (
        <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-3">
          <Tab eventKey="unvalidated" title="Unvalidated">
            {loadingFG ? (
              <p>Loading...</p>
            ) : (
              <PaginatedListTable
                ref={approvedTableRef}
                tableId="approval-list"
                columns={[...fieldColumns, ...detailColumns]}
                rowIdAccessor={row => row.id}
                onRowClick={viewEngagement}
                onFetchData={fetchApprovedEngagements}
                defaultSort={[["submittedAt", "DESC"]]}
                isLoading={loadingApproved}
                hideSearch
              />
            )}
          </Tab>
          <Tab eventKey="validated" title="Validated">
            {loadingFG ? (
              <p>Loading...</p>
            ) : (
              <PaginatedListTable
                ref={validatedTableRef}
                tableId="validated-list"
                columns={[...fieldColumns, ...detailColumns]}
                rowIdAccessor={row => row.id}
                onRowClick={viewEngagement}
                onFetchData={fetchValidatedEngagements}
                defaultSort={[["submittedAt", "DESC"]]}
                isLoading={loadingValidated}
                hideSearch
              />
            )}
          </Tab>
          {isApprovalEnabled && (
            <Tab eventKey="disapproved" title="Disapproved">
              {loadingFG ? (
                <p>Loading...</p>
              ) : (
                <PaginatedListTable
                  ref={disapprovedTableRef}
                  tableId="disapproved-list"
                  columns={[...fieldColumns, ...detailColumns]}
                  rowIdAccessor={row => row.id}
                  onRowClick={viewEngagement}
                  onFetchData={fetchDisapprovedEngagements}
                  defaultSort={[["submittedAt", "DESC"]]}
                  isLoading={loadingDisapproved}
                  hideSearch
                />
              )}
            </Tab>
          )}
          <Tab eventKey="rejected" title="Rejected">
            {loadingFG ? (
              <p>Loading...</p>
            ) : (
              <PaginatedListTable
                ref={rejectedTableRef}
                tableId="rejected-list"
                columns={[...fieldColumns, ...detailColumns]}
                rowIdAccessor={row => row.id}
                onRowClick={viewEngagement}
                onFetchData={fetchRejectedEngagements}
                defaultSort={[["submittedAt", "DESC"]]}
                isLoading={loadingRejected}
                hideSearch
              />
            )}
          </Tab>
        </Tabs>
      )}
      <ViewEngagementModal
        show={showModal}
        forms={forms}
        engagement={engagement ? { ...engagement, fromValidationPage: isNoEditMode ? true : activeTab === 'unvalidated' } : null}
        handleImageClick={handleImageClick}
        onClose={() => setShowModal(false)}
        isNoEditValidationFlow={isNoEditMode}
        validationStatusOptions={validationStatuses}
        onSubmit2ndReview={submitSecondValidation}
        onSubmitReview={activeTab === 'unvalidated' ? submitFirstValidationReview : undefined}
      />
      {showImageModal && selectedImage && (
        <ImageViewerModal
          show={showImageModal}
          imageUrl={selectedImage.src}
          footer={selectedImage.footer}
          onClose={() => setShowImageModal(false)}
        />
      )}
      <Modal show={Boolean(revertRow && isSuperAdmin)} onHide={() => !revertLoading && setRevertRow(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Revert</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to revert this engagement back to pending validation?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setRevertRow(null)} disabled={revertLoading}>
            Cancel
          </Button>
          <Button variant="warning" onClick={handleRevertToUnvalidated} disabled={revertLoading}>
            {revertLoading ? <><Spinner size="sm" className="me-1" /> Reverting...</> : 'Revert'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
export default Validation;
